/* eslint-disable */
import React, { useState, useEffect, useCallback } from 'react';
import {
  ClipboardList, Search, ChevronLeft, ChevronRight,
  RefreshCw, AlertCircle, Filter,
} from 'lucide-react';
import Page from '../../../components/Page';
import * as auditSvc from '../../../services/clientAdmin/auditLogServices';

const ACTION_META = {
  USER_LOGIN:         { label: 'Login',              color: 'bg-blue-100 text-blue-700' },
  USER_CREATED:       { label: 'User Created',       color: 'bg-green-100 text-green-700' },
  TEAM_USER_ADDED:    { label: 'User Added',      color: 'bg-purple-100 text-purple-700' },
  TEAM_USER_REMOVED:  { label: 'User Removed',    color: 'bg-red-100 text-red-700' },
  DEPARTMENT_CREATED: { label: 'Department Created', color: 'bg-indigo-100 text-indigo-700' },
  TEAM_CREATED:       { label: 'Team Created',       color: 'bg-violet-100 text-violet-700' },
};

function actionBadge(action) {
  const meta = ACTION_META[action] || { label: action, color: 'bg-gray-100 text-gray-600' };
  return (
    <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${meta.color}`}>
      {meta.label}
    </span>
  );
}

function formatDate(iso) {
  if (!iso) return '—';
  const d = new Date(iso);
  return d.toLocaleString('en-IN', {
    day: '2-digit', month: 'short', year: 'numeric',
    hour: '2-digit', minute: '2-digit',
  });
}

function SkeletonRow() {
  return (
    <tr className="animate-pulse">
      {[180, 120, 100, 120, 140].map((w, i) => (
        <td key={i} className="px-4 py-3">
          <div className={`h-3 bg-gray-200 rounded`} style={{ width: w }} />
        </td>
      ))}
    </tr>
  );
}

const PAGE_SIZE = 20;

export default function AuditLogPage() {
  const [rows, setRows]           = useState([]);
  const [total, setTotal]         = useState(0);
  const [page, setPage]           = useState(0);
  const [search, setSearch]       = useState('');
  const [actionFilter, setActionFilter] = useState('');
  const [actionTypes, setActionTypes]   = useState([]);
  const [loading, setLoading]     = useState(true);
  const [error, setError]         = useState('');

  const load = useCallback(async (pg = 0, q = search, act = actionFilter) => {
    setLoading(true);
    setError('');
    const r = await auditSvc.getAuditLog({ page: pg, size: PAGE_SIZE, search: q, action: act });
    if (r.ok) {
      setRows(r.data.data);
      setTotal(r.data.total);
      setPage(pg);
    } else {
      setError(r.data?.message || 'Failed to load audit log');
    }
    setLoading(false);
  }, [search, actionFilter]);

  useEffect(() => {
    load(0, '', '');
    auditSvc.getActionTypes().then(r => { if (r.ok) setActionTypes(r.data); });
  }, []);

  // Debounced search
  useEffect(() => {
    const t = setTimeout(() => load(0, search, actionFilter), 350);
    return () => clearTimeout(t);
  }, [search, actionFilter]);

  const totalPages = Math.ceil(total / PAGE_SIZE);

  return (
    <Page title="Audit Log">
      <div className="p-6 bg-gray-50 min-h-screen">

        {/* Breadcrumb */}
        <div className="text-sm mb-4 flex items-center gap-2 text-gray-500">
          <span className="text-gray-400">Client Admin</span>
          <span className="text-gray-300">›</span>
          <span className="text-gray-900 font-medium">Audit Log</span>
        </div>

        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-xl font-semibold text-gray-900">Audit Log</h1>
            <p className="text-sm text-gray-500">Activity history for the last 30 days</p>
          </div>
          <button
            onClick={() => load(page, search, actionFilter)}
            className="flex items-center gap-2 text-sm text-gray-600 border border-gray-300 bg-white px-3 py-2 rounded-lg hover:bg-gray-50 transition-colors"
          >
            <RefreshCw size={14} /> Refresh
          </button>
        </div>

        {/* Filters */}
        <div className="flex items-center gap-3 mb-4">
          {/* Search */}
          <div className="relative flex-1 max-w-sm">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              placeholder="Search by user or action..."
              value={search}
              onChange={e => setSearch(e.target.value)}
              className="w-full pl-8 pr-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-400 bg-white"
            />
          </div>

          {/* Action filter */}
          <div className="relative">
            <Filter size={13} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
            <select
              value={actionFilter}
              onChange={e => setActionFilter(e.target.value)}
              className="pl-8 pr-8 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-400 bg-white appearance-none"
            >
              <option value="">All Actions</option>
              {actionTypes.map(a => (
                <option key={a} value={a}>{ACTION_META[a]?.label || a}</option>
              ))}
            </select>
          </div>

          {total > 0 && (
            <span className="text-sm text-gray-400 ml-auto">{total} record{total !== 1 ? 's' : ''}</span>
          )}
        </div>

        {/* Table */}
        <div className="bg-white rounded-xl shadow-sm overflow-hidden">
          {error ? (
            <div className="p-6">
              <div className="flex items-center gap-2 p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">
                <AlertCircle size={15} />
                <span>{error}</span>
                <button onClick={() => load(page)} className="underline ml-auto">Retry</button>
              </div>
            </div>
          ) : (
            <table className="w-full text-sm">
              <thead className="bg-gray-50 border-b border-gray-100">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-400 uppercase tracking-wider">User</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-400 uppercase tracking-wider">Action</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-400 uppercase tracking-wider">Entity</th>
                  {/* <th className="px-4 py-3 text-left text-xs font-semibold text-gray-400 uppercase tracking-wider">Details</th> */}
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-400 uppercase tracking-wider">When</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50">
                {loading ? (
                  Array.from({ length: 8 }).map((_, i) => <SkeletonRow key={i} />)
                ) : rows.length === 0 ? (
                  <tr>
                    <td colSpan={5} className="py-20 text-center">
                      <ClipboardList size={36} className="mx-auto text-gray-200 mb-3" />
                      <p className="text-gray-400 text-sm">No audit log entries found.</p>
                    </td>
                  </tr>
                ) : (
                  rows.map(row => (
                    <tr key={row.id} className="hover:bg-gray-50 transition-colors">
                      {/* User */}
                      <td className="px-4 py-3">
                        <p className="font-medium text-gray-900">{row.userName}</p>
                        {row.userEmail && (
                          <p className="text-xs text-gray-400">{row.userEmail}</p>
                        )}
                      </td>

                      {/* Action badge */}
                      <td className="px-4 py-3">{actionBadge(row.action)}</td>

                      {/* Entity */}
                      <td className="px-4 py-3">
                        {row.entityType ? (
                          <span className="text-xs text-gray-500 capitalize">
                            {row.entityType} 
                          </span>
                        ) : (
                          <span className="text-gray-300">—</span>
                        )}
                      </td>

                    {/* <td className="px-4 py-3 max-w-xs">
    {row.details && Object.keys(row.details).length > 0 ? (
        <div className="relative group">
            <p className="text-xs text-gray-500 truncate cursor-pointer">
                {formatDetails(row.action, row.details)}
            </p>
            <div className="absolute z-50 left-0 top-6 hidden group-hover:block w-72 bg-gray-900 text-white text-xs rounded-lg shadow-xl p-3">
                <pre className="whitespace-pre-wrap break-all font-mono text-[11px] leading-relaxed">
                    {JSON.stringify(row.details, null, 2)}
                </pre>
            </div>
        </div>
    ) : (
        <span className="text-gray-300">—</span>
    )}
</td> */}

                      {/* Time */}
                      <td className="px-4 py-3 text-xs text-gray-500 whitespace-nowrap">
                        {formatDate(row.createdAt)}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          )}
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex items-center justify-between mt-4 text-sm text-gray-500">
            <span>Page {page + 1} of {totalPages}</span>
            <div className="flex gap-2">
              <button
                disabled={page === 0 || loading}
                onClick={() => load(page - 1)}
                className="flex items-center gap-1 px-3 py-1.5 border border-gray-200 rounded-lg hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed"
              >
                <ChevronLeft size={14} /> Previous
              </button>
              <button
                disabled={page + 1 >= totalPages || loading}
                onClick={() => load(page + 1)}
                className="flex items-center gap-1 px-3 py-1.5 border border-gray-200 rounded-lg hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed"
              >
                Next <ChevronRight size={14} />
              </button>
            </div>
          </div>
        )}
      </div>
    </Page>
  );
}

function formatDetails(action, details) {
  if (!details) return '';
  switch (action) {
    case 'USER_LOGIN':
      return `Email: ${details.email || ''}`;
    case 'USER_CREATED':
      return `${details.email || ''} · Role: ${details.roleCode || ''}`;
    case 'TEAM_USER_ADDED':
      return `${details.count || 0} learner(s) added`;
    case 'TEAM_USER_REMOVED':
      return `User #${details.removedUserId} removed`;
    case 'DEPARTMENT_CREATED':
      return `Dept: ${details.name || ''}`;
    case 'TEAM_CREATED':
      return `Team: ${details.name || ''}`;
    default:
      return JSON.stringify(details);
  }
}
